/**
 * Informe de diagnóstico en texto plano, pensado para PEGARSE tal cual.
 *
 * Nace de un caso concreto: la app se quedaba "sincronizando" para siempre y el
 * motivo era que el servicio de PowerSync ni siquiera estaba levantado. Desde la
 * pantalla no había forma de distinguir "no conecta" de "conecta y va lento", y
 * describirlo a mano se pierde la mitad de los datos.
 *
 * FUENTE ÚNICA. La primera versión recibía un objeto propio mientras la pantalla
 * pintaba sus filas por su cuenta: dos fuentes para lo mismo, así que añadir una
 * fila a la UI dejaba el informe incompleto sin que nadie se enterara. Ahora
 * ambas consumen el MISMO array de secciones: la pantalla lo renderiza, esta
 * función lo serializa y la pista se calcula leyéndolo.
 *
 * Cada elemento lleva tres campos por motivos distintos:
 *   · `label`/`value` — texto ya traducido: lo que se ve y lo que se copia.
 *   · `status`        — solo lo usa la UI para el color; el informe lo ignora.
 *   · `raw`           — valor SIN traducir, para que la lógica no dependa del
 *                       idioma. Sin él, la pista dejaría de dispararse en cuanto
 *                       alguien cambiara una traducción.
 *
 * NO incluye datos personales: ni correo, ni nombre, ni token. Quien construye
 * las secciones es responsable de recortar identificadores.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Identificador estable, independiente del idioma. Lo usa la lógica. */
  key: string;
  /** Etiqueta ya traducida. */
  label: string;
  /** Valor ya traducido / formateado, tal como se ve en pantalla. */
  value: string;
  /** Solo para la UI. */
  status?: DiagnosticStatus;
  /** Valor crudo para lógica que no debe depender del idioma. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** Marca de tiempo ISO. Se pasa; no se lee el reloj aquí (función pura). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Busca por `key`, nunca por etiqueta: las etiquetas están traducidas. */
function buscar(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const encontrado = s.items.find((i) => i.key === key);
    if (encontrado) {
      return encontrado;
    }
  }
  return undefined;
}

/**
 * Pista accionable para el patrón que originó todo esto: hay sesión y la
 * sincronización no arranca. Casi siempre es el servicio caído, no la app, y
 * decirlo aquí ahorra buscar en el sitio equivocado.
 */
function pista(sections: DiagnosticSection[]): string[] {
  const sesion = buscar(sections, "session");
  const sync = buscar(sections, "sync");
  if (
    sesion?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const partes: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const seccion of input.sections) {
    partes.push("", `## ${seccion.title}`);
    for (const item of seccion.items) {
      // Un campo vacío se MARCA en vez de omitirse: en un informe pegado, un
      // dato ausente y uno no recogido se leen igual si no se dice cuál es.
      partes.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  partes.push(...pista(input.sections));
  return partes.join("\n");
}
